import { useLayoutEffect, useRef } from 'react';

/**
 * MAINTIEN D'UN POPOVER ANCRÉ DANS LE CADRE VISIBLE.
 *
 * Tous les panneaux flottants du projet (`SortMenu`, `SavedFiltersMenu`, `ToolsMenu`,
 * `OverflowMenu`, `Menu`, `AccountSwitcher`, `NotificationBell`, `ViewerDisplayMenu`)
 * sont posés en `absolute` sur le bord d'un déclencheur : `end-0` (le panneau grandit
 * vers le début de la ligne) ou, pour le seul `ToolsMenu` étiqueté, `start-0` (il
 * grandit vers la fin). L'ancrage ne regarde QUE le déclencheur — jamais l'écran.
 *
 * DEUX AXES. Le décalage HORIZONTAL (v1.40.1) ramène le panneau dans le cadre ; le
 * bornage VERTICAL (v1.42.0) l'empêche de dépasser sous le bas de l'écran. Les deux
 * sont calculés dans la MÊME mesure, à partir du même `getBoundingClientRect`.
 *
 * BUG CORRIGÉ (v1.40.1) — sur un écran étroit, un déclencheur LOIN du bord vers lequel
 * le panneau grandit le pousse HORS CADRE, à une abscisse négative. Mesuré à 360 px CSS
 * sur la barre d'outils de la galerie, dont les boutons sont regroupés à gauche :
 * « Trier & filtrer » (bord droit à 136 px, panneau `min-w-56` = 224 px) commençait à
 * **−88 px**, « Mes filtres » (`w-64`) à **−172 px**, « Plus » (`min-w-52`) à **−148 px**.
 * Le panneau était bien rendu : c'est le DÉBUT des libellés qui sortait de l'écran
 * (« …e prise de vue », « …mélanger »).
 *
 * POURQUOI PAS `max-w-[calc(100vw-1rem)]` — `NotificationBell` la portait déjà, et elle
 * ne suffisait pas : cette classe borne la LARGEUR, pas la POSITION. Un panneau de
 * 224 px ancré à 136 px reste à −88 px même s'il « ne dépasse pas la largeur de l'écran ».
 * Elle ne sauvait la cloche que parce que SON déclencheur est collé au bord de l'en-tête.
 *
 * POURQUOI UN DÉCALAGE MESURÉ, ET PAS DU CSS — le CSS ne sait pas exprimer « ce bloc
 * doit rester dans le cadre » : le bloc conteneur est le wrapper `relative` du
 * déclencheur, dont la position dans le viewport n'est connue qu'à l'exécution.
 * (`position-try` de l'ancrage CSS le ferait, mais son support n'est pas acquis et
 * happy-dom l'ignore : le calcul ne serait plus testable hors navigateur.)
 *
 * POURQUOI `transform` — il s'applique APRÈS la mise en page : le panneau garde son
 * ancrage logique (`start-0` / `end-0`), donc le RTL reste porté par le CSS et ce
 * mécanisme n'a pas à savoir quel ancrage est actif — d'où sa capacité à couvrir les
 * DEUX variantes de `ToolsMenu` sans distinction. Il ne provoque aucun re-calcul de
 * mise en page, donc aucune boucle mesure → style → mesure.
 *
 * POURQUOI RESTER EN `absolute` (et pas `fixed`) — le panneau suit alors son déclencheur
 * au défilement sans une ligne de code ; un `fixed` resterait figé en l'air. En prime,
 * l'en-tête porte `backdrop-blur`, qui crée un bloc conteneur : un enfant `fixed` s'y
 * calerait sur la boîte de l'en-tête au lieu du viewport (piège déjà documenté dans
 * `NotificationBell`). La coquille (`AppShell`) est en `overflow-hidden` et la barre
 * d'outils passe à la ligne (`flex-wrap`) : aucun de ces panneaux ne vit dans un
 * conteneur à défilement HORIZONTAL, le décalage mesuré à l'ouverture reste donc valide.
 *
 * RTL — tout ce qui est mesuré ici (`getBoundingClientRect`, `clientWidth`) est en
 * coordonnées PHYSIQUES, et la correction rendue l'est aussi : mesurer physique et
 * corriger physique est juste dans les deux sens d'écriture. Le seul choix orienté est
 * le repli « panneau plus large que le cadre », documenté sur `clampPopoverShift`.
 *
 * BUG CORRIGÉ (v1.42.0) — VERTICALEMENT, trois panneaux seulement se bornaient, et à la
 * main : `max-h-[80vh]` (`SortMenu`), `max-h-[70vh]` (`SavedFiltersMenu`,
 * `NotificationBell`). Les CINQ autres (`Menu`, `ToolsMenu`, `OverflowMenu`,
 * `AccountSwitcher`, `ViewerDisplayMenu`) n'avaient AUCUNE hauteur maximale : sur un
 * écran court — un téléphone en paysage, une fenêtre réduite — un panneau long dépassait
 * simplement sous le bas du cadre, ses dernières entrées inatteignables.
 *
 * Ces trois classes `max-h-*` ont été RETIRÉES au profit de ce mécanisme, pour la raison
 * qui avait déjà fait retirer `max-w-[calc(100vw-1rem)]` de `NotificationBell` : une
 * demi-rustine divergente entretient la confusion sur qui borne quoi. Et une fraction de
 * `vh` est de toute façon une approximation — elle ignore où le déclencheur se trouve,
 * alors que la place réellement disponible se mesure.
 *
 * `overflow-y: auto` n'est posé QUE si le bornage a effectivement mordu. Un panneau court
 * garde donc un débordement `visible`, ce qui préserve l'anneau de focus de ses entrées :
 * un conteneur défilant ROGNE l'`outline` d'un élément collé à son bord, et ces menus se
 * parcourent au clavier (`rovingFocus`).
 *
 * NON TRAITÉ, sciemment : le RETOURNEMENT au-dessus du déclencheur quand la place
 * manque sous lui. Aucun des huit déclencheurs ne vit en bas d'écran (barre d'outils
 * galerie, en-tête, barre haute de la visionneuse), donc la place sous eux est toujours
 * l'essentiel de la hauteur. `POPOVER_MIN_HEIGHT` est le garde-fou du jour où ce ne
 * serait plus vrai : sous ce plancher, mieux vaut un panneau qui dépasse un peu qu'un
 * panneau réduit à une fente inutilisable — et c'est le signal qu'il faut retourner.
 *
 * À NE PAS CONFONDRE avec `usePopoverDismiss`, qui traite la FERMETURE de ces mêmes
 * popovers (clic extérieur, Échap, retour du focus). Les deux se composent : ce hook
 * ne touche ni au focus ni aux écouteurs, la navigation par flèches (`rovingFocus`) et
 * la fermeture restent intactes.
 */

/**
 * Marge minimale entre le panneau et chaque bord du cadre visible (px). Assez pour que
 * l'ombre portée et le coin arrondi ne soient pas rasés par le bord de l'écran.
 */
export const POPOVER_VIEWPORT_MARGIN = 8;

/**
 * Hauteur en deçà de laquelle on cesse de rétrécir un panneau (px) : sous ce seuil il
 * n'est plus utilisable, et le laisser dépasser un peu vaut mieux qu'une fente à deux
 * lignes. Cf. la note « retournement » de l'en-tête.
 */
export const POPOVER_MIN_HEIGHT = 96;

export interface PopoverHeightInput {
  /** Bord HAUT du panneau tel que le navigateur le pose (px). */
  panelTop: number;
  /** Hauteur NATURELLE du panneau (px). */
  panelHeight: number;
  /** Hauteur du cadre visible (px). */
  viewportHeight: number;
  /** Marge minimale au bord bas (px). */
  margin: number;
  /** Plancher de rétrécissement (px). */
  minHeight: number;
}

/**
 * Hauteur maximale à imposer au panneau, ou `null` s'il tient déjà sous le bord bas —
 * le cas de tous les popovers sur un écran de hauteur ordinaire.
 *
 * Le `null` n'est pas cosmétique : c'est lui qui décide de NE PAS poser
 * `overflow-y: auto`, et donc de préserver l'anneau de focus des entrées (cf. en-tête).
 */
export function clampPopoverHeight({
  panelTop,
  panelHeight,
  viewportHeight,
  margin,
  minHeight,
}: PopoverHeightInput): number | null {
  const available = viewportHeight - panelTop - margin;
  if (panelHeight <= available) return null;
  return Math.round(Math.max(minHeight, available));
}

export interface PopoverClampInput {
  /** Bord GAUCHE du panneau tel que le navigateur le pose, décalage NON compris (px). */
  panelLeft: number;
  /** Largeur du panneau (px). */
  panelWidth: number;
  /** Largeur du cadre visible, barre de défilement exclue (px). */
  viewportWidth: number;
  /** Marge minimale à chaque bord (px). */
  margin: number;
}

/**
 * Décalage horizontal (px, positif = vers la droite) ramenant le panneau dans le cadre.
 * `0` quand il y tient déjà — le cas de tous les popovers sur écran large.
 *
 * Repli quand le panneau est PLUS LARGE que le cadre (`maxLeft < minLeft`) : on colle
 * son bord GAUCHE à la marge, parce que c'est là que commencent les libellés en LTR et
 * que leur amputation est précisément le défaut corrigé. En RTL ce repli sacrifierait
 * la mauvaise extrémité — mais il est hors d'atteinte en pratique : `usePopoverClamp`
 * borne d'abord la largeur du panneau à celle du cadre.
 */
export function clampPopoverShift({
  panelLeft,
  panelWidth,
  viewportWidth,
  margin,
}: PopoverClampInput): number {
  const minLeft = margin;
  const maxLeft = viewportWidth - margin - panelWidth;
  const target = Math.min(Math.max(panelLeft, minLeft), Math.max(minLeft, maxLeft));
  return Math.round(target - panelLeft);
}

/**
 * Rend la `ref` à poser sur le PANNEAU d'un popover ancré : tant qu'il est déployé, il
 * est maintenu dans le cadre visible.
 *
 * Mesure à l'OUVERTURE et au REDIMENSIONNEMENT (jamais à chaque rendu). Réserve
 * assumée : un panneau dont le CONTENU change de largeur alors qu'il est déjà ouvert
 * n'est pas re-mesuré — les huit panneaux du projet tiennent leur largeur d'une classe
 * (`w-64`, `min-w-56`…), pas de leur contenu.
 *
 * Inerte sans moteur de mise en page (happy-dom ne mesure rien : tout est à 0) — il n'y
 * a alors aucun cadre où ramener quoi que ce soit.
 */
export function usePopoverClamp<Panel extends HTMLElement = HTMLDivElement>(
  open: boolean,
): React.RefObject<Panel | null> {
  const panelRef = useRef<Panel>(null);

  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!open || panel === null) return;

    const place = (): void => {
      // Repartir de la géométrie NATURELLE : le décalage précédent fausserait la mesure
      // (`getBoundingClientRect` rend la boîte TRANSFORMÉE).
      panel.style.transform = '';
      panel.style.maxWidth = '';
      panel.style.maxHeight = '';
      panel.style.overflowY = '';

      const viewportWidth = document.documentElement.clientWidth;
      const viewportHeight = document.documentElement.clientHeight;
      if (viewportWidth <= 0) return;

      // Borner la largeur AVANT de mesurer : un panneau plus large que le cadre ne peut
      // être sauvé par aucun décalage. L'ordre compte — la mesure qui suit tient compte
      // du rétrécissement, et le repli de `clampPopoverShift` devient inatteignable.
      const available = Math.max(0, viewportWidth - 2 * POPOVER_VIEWPORT_MARGIN);
      panel.style.maxWidth = `${available}px`;

      const rect = panel.getBoundingClientRect();
      if (rect.width <= 0) {
        panel.style.maxWidth = '';
        return;
      }

      const shift = clampPopoverShift({
        panelLeft: rect.left,
        panelWidth: rect.width,
        viewportWidth,
        margin: POPOVER_VIEWPORT_MARGIN,
      });
      panel.style.transform = shift === 0 ? '' : `translateX(${shift}px)`;

      // Bornage VERTICAL, sur la même mesure. `rect.top` est la position NATURELLE : le
      // décalage posé juste au-dessus est horizontal, il ne la déplace pas.
      if (viewportHeight > 0) {
        const maxHeight = clampPopoverHeight({
          panelTop: rect.top,
          panelHeight: rect.height,
          viewportHeight,
          margin: POPOVER_VIEWPORT_MARGIN,
          minHeight: POPOVER_MIN_HEIGHT,
        });
        if (maxHeight !== null) {
          panel.style.maxHeight = `${maxHeight}px`;
          panel.style.overflowY = 'auto';
        }
      }
    };

    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open]);

  return panelRef;
}
